import React from 'react';
import { useNavigate } from 'react-router';
import { Container } from '@/components/ui/container';
import { Stack, Inline } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';
import { ArrowRight, PlayCircle, Link2, Clock, Users, BarChart } from 'lucide-react';

function CandidateScreening() {
  const navigate = useNavigate();
  
  const features = [
    { label: 'Easy Test URL', icon: Link2, className: 'top-0 right-0' },
    { label: 'Timed Tests', icon: Clock, className: 'top-0 left-0' },
    { label: 'User Screening', icon: Users, className: 'top-0 left-0' },
    { label: 'Test Results', icon: BarChart, className: 'top-0 left-0' },
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <Container>
        <Stack spacing="xl">
          {/* Content */}
          <div className="text-center max-w-3xl mx-auto">
            <Stack spacing="lg" align="center">
              <Heading level="h1" className="text-3xl md:text-4xl lg:text-5xl font-bold">
                End-to-End Candidate Screening Made Easy
              </Heading>
              <Text size="lg" variant="muted">
                Log in, design skill-based tests, and let AI handle the rest—custom topics,
                question difficulty, timers, and result tracking—all in one place.
              </Text>
              <Inline spacing="md">
                <Button
                  size="lg"
                  onClick={() => navigate('/login_user')}
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                  }}
                  className="gap-2"
                >
                  <ArrowRight className="h-5 w-5" />
                  Get Started
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="gap-2"
                >
                  <PlayCircle className="h-5 w-5" />
                  How it Works
                </Button>
              </Inline>
            </Stack>
          </div>
          
          {/* Features Image */}
          <div className="relative w-full flex justify-center mt-12">
            <img
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/eb44707503fb3d0d0da20a89d051dbb9f4444dd810a0420bed55f5b27e1d2d02?placeholderIfAbsent=true&apiKey=7b6c1215e2f745adac7c4e4629a4a214"
              alt="Features Overview"
              className="w-full max-w-4xl object-contain rounded-lg shadow-lg z-0"
              loading="lazy"
            />
            
            {/* Feature Labels - Desktop */}
            <Card className={cn(
              "hidden md:flex absolute items-center gap-2 px-4 py-3",
              "bg-card border shadow-md hover:shadow-lg transition-all",
              "top-0 right-0"
            )}>
              <Link2 className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">Easy Test URL</span>
            </Card>
            <Card className={cn(
              "hidden md:flex absolute items-center gap-2 px-4 py-3",
              "bg-card border shadow-md hover:shadow-lg transition-all",
              "top-0 left-0"
            )}>
              <Clock className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">Timed Tests</span>
            </Card>
            <Card className={cn(
              "hidden md:flex absolute items-center gap-2 px-4 py-3",
              "bg-card border shadow-md hover:shadow-lg transition-all",
              "bottom-0 left-0"
            )}>
              <Users className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">User Screening</span>
            </Card>
            <Card className={cn(
              "hidden md:flex absolute items-center gap-2 px-4 py-3",
              "bg-card border shadow-md hover:shadow-lg transition-all",
              "bottom-0 right-0"
            )}>
              <BarChart className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">Test Results</span>
            </Card>
          </div>
          
          {/* Feature Labels - Mobile */}
          <div className="md:hidden grid grid-cols-2 gap-4 mt-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  className="flex items-center gap-2 px-4 py-3 bg-card border shadow-md"
                >
                  <Icon className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-sm">{feature.label}</span>
                </Card>
              );
            })}
          </div>
        </Stack>
      </Container>
    </section>
  );
}

export default CandidateScreening;